/**
 * Extract filename from a path (removes directory components and vault/S3 key prefix)
 *
 * Handles two prefix formats:
 * - Standard UUID format: "040240ae-8165-4953-aca0-b66200d59fe3_filename.pdf"
 * - Vault prefix format: "2c3jy7mbf8yxjj9hphyms5f86ofu-filename.pdf"
 *
 * @param path - The file path or S3 key to extract filename from
 * @returns The extracted filename without prefix, or undefined if path is empty
 */
export const extractFilename = (
  path: string | undefined | null
): string | undefined => {
  if (!path) return undefined;
  // Get the last segment after the final slash (works for both / and \)
  const parts = path.split(/[/\\]/);
  let filename = parts[parts.length - 1] || path;
  // Remove vault/S3 key prefix from filename
  // Pattern 1: Standard UUID format (e.g., "040240ae-8165-4953-aca0-b66200d59fe3_filename.pdf")
  // Pattern 2: Vault prefix format (e.g., "2c3jy7mbf8yxjj9hphyms5f86ofu-filename.pdf")
  filename = filename
    .replace(/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}[-_]/i, "")
    .replace(/^[a-z0-9]{20,}[-_]/i, "");
  return filename;
};

/**
 * Extract a clean display name from a path — strips vault/S3 prefixes AND the
 * file extension so the result is suitable for UI labels (folder headers, page
 * fallback names, tooltips).
 *
 * @param path - The file path, S3 key, or raw filename
 * @returns A human-readable name without prefix or extension, or "Unknown"
 */
export const extractDisplayName = (
  path: string | undefined | null
): string => {
  const filename = extractFilename(path);
  if (!filename) return "Unknown";
  return filename.replace(/\.[^.]+$/, "");
};

/**
 * Parse AIA page code and drawing title from a source_pdf filename.
 *
 * Used as a fallback when the backend extraction pipeline didn't populate
 * page.code / page.title (the page shows as "unnamed").
 *
 * Example: "E-229-ELECTRICAL-LIGHTING-PLAN--LEVEL-29-Rev.1.pdf"
 *   → { code: "E-229", title: "ELECTRICAL LIGHTING PLAN - LEVEL 29" }
 */
export function parsePageInfoFromFilename(
  sourcePdf: string | null | undefined
): { code: string; title: string } | null {
  const name = extractDisplayName(sourcePdf);
  if (!name || name === "Unknown") return null;

  // Match leading AIA code: E-229, A1.01, M-01, AF-100, S-201N, etc.
  const m = name.match(
    /^([A-Z]{1,3}[-.]?\d{1,4}(?:[.-]\d{1,4})?[A-Z]?)\b[-\s]*/i
  );
  if (!m) return null;

  const code = m[1].toUpperCase();
  const title = name
    .slice(m[0].length)
    .replace(/-?Rev\.?\s*\d*$/i, "")  // strip Rev.1 suffix
    .replace(/-{2,}|-/g, (m) => (m.length > 1 ? " - " : " "))  // --+ → " - ", - → " "
    .trim();

  return { code, title: title || code };
}
